import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, signInWithGoogle, signInWithGitHub, signInWithEmail, signUpWithEmail, signOut } from '../lib/supabase';
import api, { setAuthToken } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const CACHE_KEY = 'leadgen_profile_cache';

const getCachedProfile = () => {
  try {
    const cachedItem = localStorage.getItem(CACHE_KEY);
    if (!cachedItem) return null;
    
    const parsed = JSON.parse(cachedItem);
    if (parsed && parsed.timestamp) {
      if (Date.now() - parsed.timestamp < 300000) {
        return parsed.data;
      }
    }
  } catch (error) {
    // parse error or other issues
  }
  return null;
};

const setCachedProfile = (profileData) => {
  if (profileData === null || profileData === undefined) {
    localStorage.removeItem(CACHE_KEY);
  } else {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data: profileData,
      timestamp: Date.now()
    }));
  }
};

const clearCachedProfile = () => {
  localStorage.removeItem(CACHE_KEY);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    const warmUpBackend = async () => {
      try {
        console.log('Warming up backend...');
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const healthUrl = apiUrl.replace(/\/api\/?$/, '') + '/health';
        await fetch(healthUrl);
      } catch (error) {
        // silently ignore errors
      }
    };
    warmUpBackend();
  }, []);

  const fetchProfile = useCallback(async (token) => {
    try {
      console.log('Fetching profile from backend...');
      console.log('About to call /auth/me');
      
      console.log('Token exists in fetchProfile:', !!token);
      
      if (!token) {
        console.log('No token passed to fetchProfile, skipping');
        setLoading(false);
        return null;
      }

      setAuthToken(token);

      const cachedProfile = getCachedProfile();
      if (cachedProfile) {
        setProfile(cachedProfile);
        setLoading(false);
        
        // Background API call
        api.get('/auth/me', { headers: { Authorization: 'Bearer ' + token } })
          .then(res => {
            setProfile(res.data);
            setCachedProfile(res.data);
          })
          .catch(err => {
            console.error('Background profile fetch failed:', err.message);
          });
          
        return cachedProfile;
      }

      const res = await api.get('/auth/me', {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      console.log('Response status:', res.status);
      console.log('Response data:', res.data);
      
      const profileData = res.data;
      setProfile(profileData);
      setCachedProfile(profileData);
      return profileData;
    } catch (error) {
      console.error('Failed to fetch profile:', error.message);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      clearCachedProfile();
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Safety timeout
    const fallbackTimeout = setTimeout(() => {
      setLoading(prev => {
        if (prev && mounted) {
          console.warn('Auth timeout - forcing loading=false');
          return false;
        }
        return prev;
      });
    }, 5000);

    const initAuth = async () => {
      try {
        console.log('Initializing auth...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('getSession error:', error);
          if (mounted) setLoading(false);
          return;
        }

        if (!session) {
          console.log('No session found, setting loading=false');
          if (mounted) setLoading(false);
          setUser(null);
          setProfile(null);
          return;
        }

        if (session?.user && mounted) {
          console.log('Session found for:', session.user.email);
          if (session.access_token) {
            console.log('Token found:', session.access_token.substring(0, 20));
            setAuthToken(session.access_token);
          }
          setUser(session.user);
          setIsAuthenticating(true);
          await fetchProfile(session.access_token);
        } else {
          console.log('No active session');
        }
      } catch (error) {
        console.error('Auth init error:', error);
      } finally {
        if (mounted) {
          console.log('Auth init complete, setting loading=false');
          setLoading(false);
          setIsAuthenticating(false);
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth event:', event, session?.user?.email);
      if (!mounted) return;

      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
        setUser(session.user);
        
        if (session.access_token) {
          console.log('Auth state change token (first 20 chars):', session.access_token.substring(0, 20));
          setAuthToken(session.access_token);
        }

        // Only fetch profile on SIGNED_IN, not on every TOKEN_REFRESHED
        if (event === 'SIGNED_IN') {
          setIsAuthenticating(true);
          await fetchProfile(session.access_token);
          setIsAuthenticating(false);
        }
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        clearCachedProfile();
        setAuthToken(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        setIsAuthenticating(false);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(fallbackTimeout);
      subscription?.unsubscribe();
    };
  }, [fetchProfile]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await signInWithEmail(email, password);
      if (error) throw error;
      if (data?.session?.access_token) {
        setAuthToken(data.session.access_token);
      }
      return data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signup = async (email, password, name) => {
    const { data, error } = await signUpWithEmail(email, password, name);
    if (error) throw error;
    return data;
  };

  const handleLoginWithGoogle = async () => {
    const { data, error } = await signInWithGoogle();
    if (error) throw error;
    return data;
  };

  const handleLoginWithGitHub = async () => {
    const { data, error } = await signInWithGitHub();
    if (error) throw error;
    return data;
  };

  const handleLogout = async () => {
    try {
      clearCachedProfile();
      const { error } = await signOut();
      if (error) throw error;
      setAuthToken(null);
      setUser(null);
      setProfile(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear even if signOut fails
      clearCachedProfile();
      setAuthToken(null);
      setUser(null);
      setProfile(null);
    }
  };

  const value = {
    user,
    profile,
    isAuthenticated: !!user && !!profile,
    isAuthenticating: isAuthenticating || loading,
    isAdmin: profile?.is_admin === true,
    isOnboarded: profile?.is_onboarded === true,
    loading,
    login,
    signup,
    loginWithGoogle: handleLoginWithGoogle,
    loginWithGitHub: handleLoginWithGitHub,
    logout: handleLogout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
